import { FC, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  IconButton,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { SalesFunnel, FunnelStep } from '../../types';

export interface FunnelManagerProps {
  funnel: SalesFunnel;
  onUpdate: (funnel: Partial<SalesFunnel>) => Promise<void>;
}

export const FunnelManager: FC<FunnelManagerProps> = ({ funnel, onUpdate }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [stepName, setStepName] = useState('');
  const [stepColor, setStepColor] = useState('#3182CE');
  const [editingStep, setEditingStep] = useState<FunnelStep | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleAddStep = async () => {
    if (!stepName.trim() || isUpdating) return;

    const newStep: FunnelStep = {
      id: `step-${Date.now()}`,
      name: stepName,
      order: funnel.steps.length,
      color: stepColor,
    };

    setIsUpdating(true);
    try {
      await onUpdate({
        steps: [...funnel.steps, newStep],
      });
      setStepName('');
      setStepColor('#3182CE');
      onClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditStep = (step: FunnelStep) => {
    setEditingStep(step);
    setStepName(step.name);
    setStepColor(step.color || '#3182CE');
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    if (!editingStep || !stepName.trim() || isUpdating) return;

    const updatedSteps = funnel.steps.map(step =>
      step.id === editingStep.id
        ? { ...step, name: stepName, color: stepColor }
        : step
    );

    setIsUpdating(true);
    try {
      await onUpdate({
        steps: updatedSteps,
      });
      setEditingStep(null);
      setStepName('');
      setStepColor('#3182CE');
      onEditClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    if (isUpdating) return;
    setEditingStep(null);
    setStepName('');
    setStepColor('#3182CE');
    onEditClose();
  };

  const handleDeleteStep = async (stepId: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      await onUpdate({
        steps: funnel.steps.filter(s => s.id !== stepId),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReorderStep = async (stepId: string, direction: 'up' | 'down') => {
    if (isUpdating) return;
    const index = funnel.steps.findIndex(s => s.id === stepId);
    if (index === -1) return;

    const newSteps = [...funnel.steps];
    if (direction === 'up' && index > 0) {
      [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
      newSteps[index - 1].order = index - 1;
      newSteps[index].order = index;
    } else if (direction === 'down' && index < newSteps.length - 1) {
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      newSteps[index].order = index;
      newSteps[index + 1].order = index + 1;
    }

    setIsUpdating(true);
    try {
      await onUpdate({ steps: newSteps });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box p={4} bg={bgColor} border="1px" borderColor={borderColor} borderRadius="lg">
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="bold">
            Funnel Steps
          </Text>
          <Button leftIcon={<AddIcon />} size="sm" onClick={onOpen} isDisabled={isUpdating}>
            Add Step
          </Button>
        </HStack>

        <VStack align="stretch" spacing={2}>
          {funnel.steps
            .sort((a, b) => a.order - b.order)
            .map((step, index) => (
              <HStack
                key={step.id}
                p={3}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
                justify="space-between"
              >
                <HStack spacing={3}>
                  <Box
                    w="20px"
                    h="20px"
                    bg={step.color || 'blue.500'}
                    borderRadius="sm"
                  />
                  <Text>{step.name}</Text>
                </HStack>
                <HStack>
                  <IconButton
                    aria-label="Edit step"
                    icon={<EditIcon />}
                    size="xs"
                    colorScheme="blue"
                    onClick={() => handleEditStep(step)}
                    isDisabled={isUpdating}
                  />
                  <IconButton
                    aria-label="Move up"
                    icon={<Text>↑</Text>}
                    size="xs"
                    isDisabled={index === 0 || isUpdating}
                    onClick={() => handleReorderStep(step.id, 'up')}
                  />
                  <IconButton
                    aria-label="Move down"
                    icon={<Text>↓</Text>}
                    size="xs"
                    isDisabled={index === funnel.steps.length - 1 || isUpdating}
                    onClick={() => handleReorderStep(step.id, 'down')}
                  />
                  <IconButton
                    aria-label="Delete step"
                    icon={<DeleteIcon />}
                    size="xs"
                    colorScheme="red"
                    onClick={() => handleDeleteStep(step.id)}
                    isDisabled={isUpdating}
                  />
                </HStack>
              </HStack>
            ))}
        </VStack>

        <Modal isOpen={isOpen} onClose={isUpdating ? () => {} : onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add Funnel Step</ModalHeader>
            <ModalCloseButton isDisabled={isUpdating} />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Step Name</FormLabel>
                  <Input
                    value={stepName}
                    onChange={(e) => setStepName(e.target.value)}
                    placeholder="e.g., Contacted"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Color</FormLabel>
                  <Input
                    type="color"
                    value={stepColor}
                    onChange={(e) => setStepColor(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleAddStep} isLoading={isUpdating} loadingText="Adding...">
                Add
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isEditOpen} onClose={isUpdating ? () => {} : handleCancelEdit}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Funnel Step</ModalHeader>
            <ModalCloseButton isDisabled={isUpdating} />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Step Name</FormLabel>
                  <Input
                    value={stepName}
                    onChange={(e) => setStepName(e.target.value)}
                    placeholder="e.g., Contacted"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Color</FormLabel>
                  <Input
                    type="color"
                    value={stepColor}
                    onChange={(e) => setStepColor(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleCancelEdit} isDisabled={isUpdating}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSaveEdit} isLoading={isUpdating} loadingText="Saving...">
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

